import sha256 from 'js-sha256';
import {
  SRCI_CANCEL_RESPONSE,
  SRCI_FAILURE_RESPONSE,
  SRCI_SUCCESS_RESPONSE,
  SRC_CANCEL_RESPONSE_RECEIVED,
  SRC_FAILURE_RESPONSE_RECEIVED,
  SRC_SUCCESS_RESPONSE_RECEIVED
} from '../../src/constants/gtm-events';
import isEmptyObject from '../../src/helpers/is-empty-object';
import { GtmInstr } from '../../src-i-adapter/helpers/gtm';
import { apiErrors, apiMethodnames } from '../constants/common';
import * as DATAELEMENTS from '../constants/custom-data-elements';
import DCF_ACTIONS from '../constants/dcf-action-codes';
import * as EVENTS from '../constants/external-events';
import { MA, MASTERCARD, VISA } from '../constants/partnering-networks';
import collapsedLogger from '../helpers/collapsed-logger';
import getIdToken from '../helpers/get-id-token';
import getNetworkInstance from '../helpers/get-network-instance';
import { enrichDataLayer, getGtmData, trackINOEvent } from '../helpers/gtm';
import isCustomDcfErrorEvent from '../helpers/is-custom-dcf-error';
import isEnrollCard from '../helpers/is-enroll-card-checkout';
import isWindow from '../helpers/is-window';
import logger from '../helpers/logger';
import OrchCustomData from '../helpers/orch-custom-data';
import resetCustomData from '../helpers/reset-data';
import sendMessageToVisaSrci from '../helpers/send-message-to-visa-srci';
import updateDpaLocale from '../helpers/update-dpa-locale';
import unbindAppInstance from './unbind-app-instance';
/**
 * Launch Visa/Non Visa DCF base don the input data object
 * For add new card usecase, below data are needed
 *  - idToken {optional}
 *  - srcCorrelationId {optional}
 *  - encryptedCard {mandatory}
 * For select card usecase, below data are needed
 *  - srcCorrelationId {mandatory}
 *  - srcDigitalCardId {mandatory}
 * @param {object} inputData
 * @return {promise} promise
 */
const checkout = inputData => {
  return new Promise(resolve => {
    OrchCustomData.isCheckoutInvoked = true;

    const promiseWrapper = function(instance, instanceName, data, method) {
      if (instance === null || instance === undefined) {
        const resultData = {
          error: apiErrors.NO_INSTANCE
        };
        return Promise.reject(resultData);
      }
      /* On listening to this event, the app sets a global variable which is checked to emit the 'cancel'
    * event whenever onbeforeunload event is emitted by the browser and the callback that's binded to the event is executed.
    * */
      sendMessageToVisaSrci(EVENTS.SRC_APP_TRANSITIONING);

      return instance[method](data)
        .then(res => Promise.resolve(res))
        .catch(error => Promise.reject(error));
    };
    // Load dataLayer from SRCi to GTM
    const data = enrichDataLayer(inputData);
    const gtmData = getGtmData(data);
    const dataLayer = inputData.dataLayer;

    const showVdcpIno = OrchCustomData.srcSdks?.orchConfig?.showVdcpIno ?? false;
    const isEnroll = isEnrollCard(data);
    let intentId = '';

    const cardBrand = data.cardBrand;
    if (!isEnroll) {
      delete data.cardBrand;
    }
    let dcf;
    if (showVdcpIno) {
      //Default DCF to VISA if cardBrand is not present
      dcf = cardBrand || VISA;
      //TODO remove MA implementation across orch layer
      dcf = dcf === MASTERCARD ? MA : dcf;
    } else {
      dcf = VISA;
    }

    const eventId = EVENTS.ORCH_CHECKOUT;
    //set window reference
    const windowRef = OrchCustomData.srcSdks?.orchConfig?.windowReference ?? null;
    collapsedLogger('-- Checkout API -- with input from SRCi', data);
    logger('-- Launch DCF input data from srci', data);
    const { isCardFirstFlow, sdkCerts, srcCorrelationId } = data;
    const checkoutData = {
      srcCorrelationId,
      windowRef
    };

    if (OrchCustomData.isVsb && OrchCustomData.isVsbButtonless) {
      checkoutData.dataLayer = {
        correlation_id: OrchCustomData.correlationId,
        xo_visitid: OrchCustomData?.initData?.VISA?.data?.visitId ?? 'undefined'
      };
    }

    let baseRequestPayload;
    // check for enroll card, then get IdToken
    if (isEnroll) {
      const idTokens = getIdToken();
      if (idTokens?.length >= 1 && dcf !== VISA) {
        // Set any one idToken if there are more than 1 toke
        checkoutData.idToken = idTokens[0];
      }
      if (dcf === VISA) {
        const enrollCardIdToken =
          OrchCustomData.srcSdks?.apiResponseCache?.getSrcProfile?.VISA?.profiles?.[0]?.idToken;
        if (enrollCardIdToken) {
          checkoutData.idToken = enrollCardIdToken;
        }
      }
      checkoutData.encryptedCard = data.encryptedCard;
    } else {
      const allProfiles = OrchCustomData.srcSdks.apiResponseCache.getSrcProfile;
      for (const network in allProfiles) {
        const networkProfile = allProfiles[network].profiles;
        if (!isEmptyObject(networkProfile)) {
          for (const profile of networkProfile) {
            //VCO-15382 -> User has a only single Discover card in account and deleting the same card from  user's profile
            const cards = profile.maskedCards || [];
            for (const card of cards) {
              if (card.srcDigitalCardId === data.srcDigitalCardId) {
                dcf = network;
              }
            }
          }
        }
      }
      const srcProfiles = OrchCustomData.srcSdks.apiResponseCache.getSrcProfile[dcf].profiles;
      const profile = srcProfiles.find(srcProfile =>
        srcProfile.maskedCards.some(
          maskedCard => maskedCard.srcDigitalCardId === data.srcDigitalCardId
        )
      );
      checkoutData.idToken = profile.idToken || profile.authorization;
      OrchCustomData.srcSdks.orchConfig.dcfIdToken = checkoutData.idToken;
      checkoutData.srcDigitalCardId = data.srcDigitalCardId;
      if (!checkoutData.srcCorrelationId) {
        checkoutData.srcCorrelationId = allProfiles[dcf]?.srcCorrelationId;
      }
    }

    //based on cardBrand (receives from SRCi) set the instance
    const sdkInstance = getNetworkInstance(OrchCustomData, dcf);
    const vco = getNetworkInstance(OrchCustomData, VISA);

    if (dcf !== VISA) {
      intentId =
        OrchCustomData.srcSdks?.apiResponseCache?.getSrcProfile?.VISA?.srcCorrelationId ?? '';
      baseRequestPayload = btoa(
        unescape(encodeURIComponent(JSON.stringify(OrchCustomData.intentRequestPayload)))
      );
      checkoutData.srciTransactionId = sha256(
        `${baseRequestPayload}${OrchCustomData.apikey}${intentId ? intentId : ''}`,
        { algorithm: 'sha256' }
      );

      // VCO-11713: save the sdk certs to visa sys-sdk in the event a non-visa DCF returns back to Visa SRCi
      if (vco) {
        vco.updateVdcpConfig({ sdkCerts });
      }
    }

    OrchCustomData.apiTimeStamp.checkout = {};
    OrchCustomData.apiTimeStamp.checkout[dcf] = {};
    // For Visa DCF, merge all the input data which has additional params
    // like locale, countryCode and isCarfFirstEntry flags
    if (dcf === VISA) {
      Object.assign(checkoutData, data);

      if (isEnroll) {
        checkoutData.addCardInfo = data.addCardInfo;
      }
    } else {
      if (windowRef && !isWindow(windowRef)) {
        // VCO-13386 - set to true if the DCF only supports modal. Currently MA and Amex both only support modal
        const isModalOnly = true; // add logic here if future onboarded DCFs support a takeover mode
        const message = `visa.src.show-launch-loader--${JSON.stringify({ isModalOnly })}`;

        // VCO-12056 - window.origin is undefined in Edge
        window.origin
          ? window.postMessage(message, window.origin)
          : window.postMessage(message, window.location.origin);
      }

      // Generalize windowRef to all Ext-SRCs as part of Discover feature
      checkoutData.windowRef = windowRef
        ? isWindow(windowRef)
          ? windowRef
          : windowRef.contentWindow
        : '';
    }

    if (data.srciActionCode) {
      checkoutData.srciActionCode = data.srciActionCode;
    }

    //update DPA locale for external partner network DCF
    if (data.locale) {
      const initData = OrchCustomData.initData;
      for (const dcf in initData) {
        if (dcf !== VISA) {
          updateDpaLocale(initData[dcf], data.locale);
        }
      }
    }

    //set dpaTransactionOptions for all networks except VISA
    if (dcf !== VISA) {
      checkoutData.dpaTransactionOptions = OrchCustomData.initData[dcf].dpaTransactionOptions;
    }

    OrchCustomData.apiTimeStamp.checkout[dcf].startTime = Date.now();
    collapsedLogger('-- final constructed checkoutData', checkoutData);
    logger('-- Launch DCF ', dcf);
    logger('-- Launch DCF checkoutData', checkoutData);
    checkoutData.srciActionCode = data.srciActionCode ? data.srciActionCode : '';
    //invoke checkout with corresponding network instance
    const sdkInstancePromise = promiseWrapper(
      sdkInstance,
      dcf,
      checkoutData,
      apiMethodnames.CHECKOUT
    );
    sdkInstancePromise
      .then(response => {
        // vsb checkout response
        GtmInstr.trackCheckoutInitiation(checkoutData, 'Response', 'Success');
        collapsedLogger('-- checkout response', response);
        OrchCustomData.isCheckoutInvoked = false;
        OrchCustomData.isGetSRCProfileInvoked = false;
        if (response) {
          if (response.idToken) {
            OrchCustomData.updateIdToken(response.idToken);
          }
          const dcfActionCode = response.dcfActionCode;
          if (dcfActionCode === DCF_ACTIONS.CANCEL) {
            OrchCustomData.srcSdks.orchConfig.windowReference = null;
            OrchCustomData.clearApiResponseCache();
            trackINOEvent(SRC_CANCEL_RESPONSE_RECEIVED, gtmData);
            trackINOEvent(SRCI_CANCEL_RESPONSE, null);
          } else if (dcfActionCode === DCF_ACTIONS.ERROR) {
            OrchCustomData.srcSdks.orchConfig.windowReference = null;
            trackINOEvent(SRC_FAILURE_RESPONSE_RECEIVED, gtmData);
            trackINOEvent(SRCI_FAILURE_RESPONSE, gtmData);
          } else if (dcfActionCode === DCF_ACTIONS.COMPLETE) {
            OrchCustomData.srcSdks.orchConfig.windowReference = null;
            OrchCustomData.clearApiResponseCache();
            trackINOEvent(SRC_SUCCESS_RESPONSE_RECEIVED, gtmData);
            trackINOEvent(SRCI_SUCCESS_RESPONSE, gtmData);
          }
          //call unBind unbindAppInstance is set true
          if (response && response.unbindAppInstance) {
            unbindAppInstance({ idToken: response.idToken });
            resetCustomData(DATAELEMENTS.IS_RECOGNIZED);
            OrchCustomData.clearApiResponseCache();
            collapsedLogger('unbind api input data from Orch', data);
          }
          const step = `ORCH_CHECKOUT_${dcf}_API_SUCCESS_RESPONSE`;
          OrchCustomData.apiTimeStamp.checkout[dcf].endTime = Date.now();
          if (vco && typeof vco.pushEvents === 'function') {
            vco.pushEvents({
              E: step,
              ERRCODE: null,
              ERRMSG: null,
              ETIME: OrchCustomData.apiTimeStamp.checkout[dcf].endTime,
              LL: 'INFO',
              S: 200,
              STIME: OrchCustomData.apiTimeStamp.checkout[dcf].startTime,
              TY: 'RESPONSE',
              checkout_step: step,
              srcInitiatorId: OrchCustomData.srcInitiatorId[dcf]
            });
          }
          // eslint-disable-next-line
          if (dcf !== VISA) {
            if (dcfActionCode === DCF_ACTIONS.COMPLETE || dcfActionCode === DCF_ACTIONS.CANCEL) {
              vco.handleResponse({
                baseRequestPayload,
                dataLayer,
                gtmData,
                intentId,
                network: dcf,
                response
              });
            } else if (!OrchCustomData.isVsbButtonless) {
              vco.reLaunchSrcI(dcfActionCode, windowRef, isCardFirstFlow);
            }
          } else {
            // send to srcUI via postmessage
            sendMessageToVisaSrci(eventId, response);
          }

          //sets the response on orchlayer before send to SRCi
          OrchCustomData.setCheckoutResponse({
            response
          });
        }
        const merchantEvent = [DCF_ACTIONS.CANCEL, DCF_ACTIONS.ERROR, DCF_ACTIONS.COMPLETE];
        if (merchantEvent.includes(response?.dcfActionCode)) {
          const promise = new Promise(resolve => {
            OrchCustomData.checkoutCallback = (type, data) => {
              resolve({ dcfActionCode: type, dpaCheckoutResponse: data });
            };
          });
          promise.then(res => {
            resolve(res);
          });
        } else {
          resolve({ dcfActionCode: response?.dcfActionCode });
        }
      })
      .catch(checkoutError => {
        GtmInstr.trackCheckoutInitiation(checkoutData, 'Response', 'Error');
        trackINOEvent(SRC_FAILURE_RESPONSE_RECEIVED, gtmData);
        trackINOEvent(SRCI_FAILURE_RESPONSE, null);
        //VCO-15021 - Handle non-std error with reasonCode
        const dcfActionCode =
          checkoutError?.dcfActionCode || checkoutError?.reason || checkoutError?.reasonCode;
        const stepErr = `ORCH_CHECKOUT_${dcf}_API_ERROR_RESPONSE`;
        if (vco && typeof vco.pushEvents === 'function') {
          vco.pushEvents({
            E: stepErr,
            ERRCODE: dcfActionCode ? dcfActionCode : '',
            ERRMSG: checkoutError.message ? checkoutError.message : '',
            ETIME: OrchCustomData.apiTimeStamp.checkout[dcf].endTime,
            LL: 'INFO',
            S: null,
            STIME: OrchCustomData.apiTimeStamp.checkout[dcf].startTime,
            TY: 'RESPONSE',
            checkout_step: stepErr,
            response: checkoutError,
            srcInitiatorId: OrchCustomData.srcInitiatorId[dcf]
          });
        }
        OrchCustomData.setCheckoutResponse(checkoutError);
        const isCardFirst = inputData && inputData.hasOwnProperty('srciActionCode');
        //VCO-12022 => on non-visa DCF failure redirect to merchant
        if (dcf !== VISA) {
          if (isCustomDcfErrorEvent(dcfActionCode, isCardFirst)) {
            vco.handleResponse({
              baseRequestPayload,
              intentId,
              network: dcf,
              response: { dcfActionCode: DCF_ACTIONS.CANCEL }
            });
            trackINOEvent(SRC_CANCEL_RESPONSE_RECEIVED, gtmData);
            trackINOEvent(SRCI_CANCEL_RESPONSE, gtmData);
          }
          //Needed to handle errors from Visa SRCi if it comes through CFE
          sendMessageToVisaSrci(eventId, checkoutError);
        } else {
          // send to srcUI via postmessage
          sendMessageToVisaSrci(eventId, checkoutError);
        }
        resolve(checkoutError);
      });
    collapsedLogger('After checkout updated OrchCustomData => ', OrchCustomData);
  });
};

export default checkout;
